import React, { useMemo } from 'react';
import './DataFilter.css';
import { Header } from '@/Shared/Types/datatable.types';
import { useDataTableContext } from '@/Shared/Logic/Contexts/DataTableContext';
import { FaSort, FaUndo } from '@/Shared/Lib/UI/Icons';
import { RecordHelper } from '@/Shared/Helpers/record.helper';
import { useBreakPoint } from '@/Shared/Logic/Hooks/TechnicalHooks/useBreakPoint';
import RowsPerPagePicker from '../RowsPerPagePicker/RowsPerPagePicker';
import { AppEnum, ExportFilter } from '@/Shared/Types';
import { ReactSelectField } from '@/Shared/UI/Forms';

interface DataFilterProps {
	isModal?: boolean;
	parentId? : string
}

/**
 * Component: DataFilter
 *
 * Description:
 * This reusable component renders dynamic filter fields for a data table based on column configuration.
 * Supports text input filtering and select-based filtering for columns with predefined options.
 * Works responsively: adjusts UI for desktop and non-desktop breakpoints.
 *
 * Responsibilities:
 * - Render a filter field per header with a `filter` property
 * - Handle input change events and delegate them to the `DataTable` context
 * - Provide a reset button for clearing all filters
 * - Integrate with `RowsPerPagePicker` for pagination control
 *
 * Hooks used:
 * - useDataTableContext: exposes active filters, headers, and filter handling functions
 * - useBreakPoint: provides responsive breakpoint awareness
 *
 * Author: Samano CASTRE
 * Date: June 29, 2025
 */
const DataFilter = <VM extends Record<string, any>,>({ isModal, parentId } : DataFilterProps) => {

	/**
	 * Responsive breakpoint info.
	 * isDesktop is used to adjust UI layout and filter button placement.
	 */
	const { isDesktop } = useBreakPoint();

	/**
	 * Extract relevant table context values.
	 */
	const { headers, sort, handleSortChange, handleFilterChange, activeFilters, resetFilter } = useDataTableContext<VM>();


	/**
	 * Memoized list of headers that support filtering.
	 * Prevents unnecessary re-renders when headers do not change.
	 */
	const getFilterableHeaders = useMemo(() => RecordHelper.toValues(headers).filter(header => !!header.filter), [headers]);


	const getSortableHeaders = useMemo(() => RecordHelper.toValues(headers).filter(header => !!header.isSortable), [headers]);


	/**
	 * Renders the appropriate filter field (text input or select) for a given header.
	 *
	 * @param header The column header configuration
	 * @param index Key for list rendering
	 * @returns JSX element representing the filter field
	 */
	const getField = (header: Header<VM>) => {

		const key = header.key as string

		const filter = activeFilters ? RecordHelper.get(activeFilters, key) as ExportFilter : undefined;
		const isSelectFilter = !!header.filter?.options;


		// Render select dropdown for headers with predefined options
		if (isSelectFilter) {
			return (
				<ReactSelectField
					key={key}
					label={header.filter?.label || header.label}
					icon={header.icon && <div className='field-icon'>{header.icon}</div>}
					name={key}
					options={header.filter?.options ?? []}
					field={{
						name: key,
						value: filter?.value ?? ""
					}}
					form={{
						setFieldValue: (name: string, value: string) => {
							return handleFilterChange(header, value);
						}
					}}
					scrollParent={ document.querySelector('#' + (parentId ?? 'DataFilter')) as HTMLDivElement}
				/>
			);
		}

		// Render standard text input for other headers
		return (
			<label className="data-field-label" key={key}>
				{header.filter?.label || header.label}
				<div className="form-group">
					<div className='Field-wrapper'>
						{header.icon && <div className='field-icon'>{header.icon}</div>}
						<input
							type="search"
							value={filter?.value ?? ""}
							onChange={(e) => handleFilterChange(header, e.target.value)}
							placeholder={"Saisir " + header.label}
							className="input-field"
						/>
					</div>
				</div>
			</label>);
	}


	const getSortableOptions = () => {
		const options: AppEnum[] = [];
		(getSortableHeaders ?? []).forEach(header => {
			const key = String(header.key);
			const value = String(header.key);
			const label = header.label;

			options.push({
				key: key + "-asc",
				value: value + "-asc",
				label: label + " - ASC"
			},
				{
					key: key + "-desc",
					value: value + "-desc",
					label: label + " - DESC",
				});
		});
		return options;
	}

	return (
		<div className="DataFilter" data-testid="DataFilter" id="DataFilter">
			{
				/* Reset filters button shown only when filters are active and on mobile */
				(!RecordHelper.isEmpty(activeFilters) && !isDesktop) &&
				<div className="data-filter-container">
					<button
						className='reset-filter-button button with-icon'
						title='Annuler tous les filtres'
						onClick={resetFilter}> <FaUndo /> Annuler filtres
					</button>
				</div>
			}

			{/* Filter container */}
			<div className="data-filter-container">
				<h3 className='data-filter-title'>Filtrer les données</h3>
				{getFilterableHeaders.map(getField)}
                { isModal && 
				<div className="data-sort-container" id='dataSort'>
					<ReactSelectField
						label="Trier par"
						icon={<FaSort />}
						name="dataSortContainer"
						options={getSortableOptions()}
						field={{
							name: "dataSortContainer",
							value: sort?.column ? String(sort.column) + '-' + String(sort.order) : ""
						}
						}
						form={{
							setFieldValue: (name: string, value: string) => {
								return handleSortChange(value);
							}
						}}
						scrollParent={document.querySelector('#dataSort') as HTMLDivElement} />
				</div>}
				<RowsPerPagePicker />

			</div>
		</div>
	);
}

export default DataFilter;